import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePreferenciaGrid, useSalvarPreferenciaGrid } from './usePreferenciasGrid';

/**
 * Preferências de notificação POR USUÁRIO — o que chega no sino dele. Guardadas em
 * `utilyhub.usuario_preferencia` (padrão do projeto pra config por-usuário; a tabela
 * serve pra qualquer estado persistente, não só grid), sem tabela nova.
 *
 * Semântica **opt-out**: ausência de preferência = recebe tudo. Guardamos só o que
 * o usuário SILENCIOU — assim gatilho novo já nasce chegando pra todo mundo, sem
 * precisar de migração de preferência.
 *
 * Categoria = prefixo do `tipo` (ex.: "cte.baixado" → "cte"). O backend filtra
 * genericamente por prefixo (feed + contagem), então não há catálogo espelhado.
 */
const GRID_ID = 'notificacoes-prefs';

export interface NotificacaoPrefs {
  silenciados: string[];
}

const parse = (json?: string | null): NotificacaoPrefs => {
  if (!json) return { silenciados: [] };
  try {
    const o = JSON.parse(json);
    return {
      silenciados: Array.isArray(o?.silenciados)
        ? o.silenciados.filter((s: unknown): s is string => typeof s === 'string')
        : [],
    };
  } catch {
    return { silenciados: [] }; // JSON corrompido → modo seguro: recebe tudo
  }
};

export function useNotificacaoPrefs() {
  const qc = useQueryClient();
  const { data: pref, isLoading } = usePreferenciaGrid(GRID_ID);
  const salvar = useSalvarPreferenciaGrid(GRID_ID);

  const prefs = useMemo(() => parse(pref?.estadoJson), [pref?.estadoJson]);

  const silenciado = useCallback(
    (prefixo: string) => prefs.silenciados.includes(prefixo),
    [prefs.silenciados],
  );

  /** `ativo=true` → volta a receber; `false` → silencia a categoria. */
  const alternar = useCallback(
    (prefixo: string, ativo: boolean) => {
      const set = new Set(prefs.silenciados);
      if (ativo) set.delete(prefixo);
      else set.add(prefixo);
      salvar.mutate(JSON.stringify({ silenciados: [...set] }), {
        // O GET de notificações filtra pelas prefs — sem invalidar, o feed/badge
        // continuariam mostrando a categoria recém-silenciada até o próximo refetch.
        onSuccess: () => qc.invalidateQueries({ queryKey: ['notificacoes'] }),
      });
    },
    [prefs.silenciados, salvar, qc],
  );

  return { prefs, silenciado, alternar, isLoading, salvando: salvar.isPending };
}
